import { Component } from "@theme/component";
import { mediaQueryLarge, isMobileBreakpoint, isDesktopBreakpoint } from "@theme/utilities";

// Single pre-bundled file (jsDelivr /+esm) instead of swiper-bundle.min.mjs, which pulls in ~30 separate module files.
// Imported on demand, the first time a carousel comes near the viewport, so it stays off the initial page load.
const SWIPER_URL = 'https://cdn.jsdelivr.net/npm/swiper@11.2.10/swiper-bundle.min.mjs/+esm';
/** @type {Promise<any>|null} */
let swiperPromise = null;
const loadSwiper = () => (swiperPromise ??= import(SWIPER_URL).then((module) => module.default));

export class AstCarouselComponent extends Component {
    /**@type {Swiper|null} */
    #swiperInstance = null;

    #abortController = new AbortController();

    /**@type {Number | undefined} */
    #cardInstantiationTimeout = undefined;

    /**@type {IntersectionObserver|null} */
    #viewObserver = null;

    #isNearViewport = false;

    /** Incremented on every view change so a slow Swiper import cannot initialise a stale view */
    #viewToken = 0;

    connectedCallback() {
        super.connectedCallback();
        const { signal } = this.#abortController;
        mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange.bind(this), { signal });

        this.#viewObserver = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.#viewObserver?.disconnect();
            this.#viewObserver = null;
            this.#isNearViewport = true;
            this.#handleView();
        }, { rootMargin: '300px 0px' });
        this.#viewObserver.observe(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#viewObserver?.disconnect();
        this.#viewObserver = null;
    }

    /**
     * @param {Event} event
     */
    #handleMediaQueryChange(event) {
        /**@type {MediaQueryList|EventTarget|null} */
        const target = event.target;
        // Not set up yet: the IntersectionObserver picks the right view when the carousel comes near the viewport
        if (!this.#isNearViewport) return;
        // Mobile
        if (target instanceof MediaQueryList && target.media === '(width >= 750px)' && !target.matches) {
            if (this.isCarouselOnMobile) {
                this.#initCarouselView();
            } else {
                this.#initGridView();
            }
        } 
        // Desktop
        else {
            if (this.dataset.layoutType === 'grid') {
                this.#initGridView();
            } else {
                this.#initCarouselView();
            }
        }
    }

    #handleView() {
        if ((isMobileBreakpoint() && this.isCarouselOnMobile) || (isDesktopBreakpoint() && this.isCarouselLayout)) {
            this.#initCarouselView();
        } else {
            this.#initGridView();
        }
    }

    getOptions() {
        const comp = this;

        /**
         * @param {Swiper} swiper
         */
        const reInstantiateCards = (swiper) => {
            for (let slide of swiper.slides) {
                const card = slide.querySelector('product-card-link');
                if (!card) return;
                const newCard = card.cloneNode(true);
                if (newCard.hasAttribute('data-unique-id')) {
                    newCard.removeAttribute('data-unique-id');
                }
                newCard.setAttribute('data-unique-id', String(Math.random() * 100));
                card.replaceWith(newCard);
            }
        };

        return ({
            loop: this.dataset.loop != 'true' ? false : true,
            mousewheel: this.dataset.mousewheel != 'true' ? false : true,
            slidesPerView: Number(this.dataset.slidesToShowMobile),
            spaceBetween: Number(this.dataset.gapMobile),
            centeredSlides: false,
            navigation: {
                nextEl: this.querySelector('.swiper-button-next'),
                prevEl: this.querySelector('.swiper-button-prev')
            },
            breakpoints: {
                750: {
                    spaceBetween: Number(this.dataset.gapDesktop),
                    slidesPerView: 2
                },
                990: {
                    spaceBetween: Number(this.dataset.gapDesktop),
                    slidesPerView: Number(this.dataset.slidesToShowTablet)
                },
                1200: {
                    spaceBetween: Number(this.dataset.gapDesktop),
                    slidesPerView: Number(this.dataset.slidesToShowDesktop)
                }
            },
            pagination: {},
            on: {
                init: (/**@type {Swiper} */swiper) => {
                    swiper.el.style.opacity = 1;
                    swiper.el.style.height = 'auto';
                    this.querySelector('.swiper-wrapper')?.classList.remove('grid')
                },
                activeIndexChange: (/**@type {Swiper} */swiper) => {
                    if (comp.dataset.instantiateActive != 'true') return;
                    clearTimeout(this.#cardInstantiationTimeout);
                    this.#cardInstantiationTimeout = setTimeout(() => reInstantiateCards(swiper), 1800);
                }
            }
        });
    }

    async #initCarouselView() {
        if (!(this.refs.slider instanceof HTMLElement)) throw new Error('Slider element not found.');

        const token = ++this.#viewToken;
        const Swiper = await loadSwiper();
        if (token !== this.#viewToken || !this.isConnected) return;

        if (this.wrapper && this.wrapper.classList.contains('grid')) {
            this.wrapper.classList.remove('grid');
        }

        if (this.#swiperInstance) {
            this.#swiperInstance.destroy();
        }

        let options = this.getOptions();

        if (isMobileBreakpoint() && this.isCenteredOnMobile) {
            const pagination = {
                el: '.swiper-pagination',
                clickable: true,
                enabled: true,
            };
            options = {...options, pagination, centeredSlides: true};
        }

        this.#swiperInstance = new Swiper(this.refs.slider, options);
    }

    #initGridView() {
        this.#viewToken++;
        if (this.#swiperInstance) {
            this.#swiperInstance.destroy();
            this.#swiperInstance = null;
        }
        if (this.wrapper && !this.wrapper.classList.contains('grid')) {
            this.wrapper.classList.add('grid');
        }
    }

    /**
     * @type {Element|Element[]|undefined}
     */
    get slider() {
        return this.refs.slider
    }

    /**
     * @type {HTMLElement|null}
     */
    get wrapper() {
        return this.querySelector('.swiper-wrapper');
    }

    /**
     * @type {Boolean}
     */
    get isCarouselOnMobile() {
        return this.dataset.carouselOnMobile == 'true';
    }

    /**
     * @type {Boolean}
     */
    get isCenteredOnMobile() {
        return this.dataset.centeredOnMobile == 'true';
    }

    /**
     * @type {Boolean}
     */
    get isGridLayout() {
        return this.dataset.layoutType == 'grid';
    }

    /**
     * @type {Boolean}
     */
    get isCarouselLayout() {
        return this.dataset.layoutType == 'carousel';
    }
}

if (!customElements.get('ast-carousel')) {
    customElements.define('ast-carousel', AstCarouselComponent);
}